// `Doubling` (SuiteStory/Doubling.swift; stage plays design, step 7): characters never on stage in the same scene can be
// played by one actor. The fewest actors is the fewest colours of the graph joining characters who share a scene; on
// stage = on-stage or POV, never a mention. Exact for up to 24 who appear (within a step budget), else DSatur.
import type { PresenceLevel } from '../presence.ts';

export interface DoublingActor { roles: string[]; scenes: number }
export interface QuickChange { from: string; to: string; scene: string }
export interface Doubling { actors: DoublingActor[]; quickChanges: QuickChange[]; characters: number }

export function suggestDoubling(order: readonly string[], presence: readonly { scene: string; entity: string; level: PresenceLevel }[],
  characters: readonly string[]): Doubling {
  const cast = new Set(characters), scenes = new Set(order);
  const inScene = new Map<string, Set<string>>();
  for (const row of presence) {
    if ((row.level === 'on-stage' || row.level === 'pov') && cast.has(row.entity) && scenes.has(row.scene)) {
      if (!inScene.has(row.scene)) inScene.set(row.scene, new Set());
      inScene.get(row.scene)!.add(row.entity);
    }
  }
  const count = new Map<string, number>();
  for (const s of order) for (const e of inScene.get(s) ?? []) count.set(e, (count.get(e) ?? 0) + 1);
  const people = characters.filter((c) => count.has(c));
  if (!people.length) return { actors: [], quickChanges: [], characters: 0 };
  const index = new Map(people.map((p, i) => [p, i]));
  const together = people.map(() => new Set<number>());
  for (const s of order) {
    const here = [...(inScene.get(s) ?? [])].map((e) => index.get(e)!).filter((i) => i !== undefined);
    for (const a of here) for (const b of here) if (a !== b) together[a].add(b);
  }
  const colours = colourGraph(together);
  const groups = new Map<number, number[]>();
  colours.forEach((c, person) => groups.set(c, [...(groups.get(c) ?? []), person]));
  const actors = [...groups.values()].map((members) => {
    const roles = [...members].sort((x, y) => count.get(people[y])! - count.get(people[x])! || x - y).map((i) => people[i]);
    return { roles, scenes: roles.reduce((n, r) => n + count.get(r)!, 0) };
  });
  actors.sort((a, b) => count.get(b.roles[0])! - count.get(a.roles[0])! || index.get(a.roles[0])! - index.get(b.roles[0])!);
  const quickChanges: QuickChange[] = [];
  for (const actor of actors) {
    if (actor.roles.length < 2) continue;
    const roles = new Set(actor.roles);
    for (let i = 0; i + 1 < order.length; i++) {
      for (const from of inScene.get(order[i]) ?? []) {
        if (!roles.has(from)) continue;
        for (const to of inScene.get(order[i + 1]) ?? []) if (roles.has(to) && to !== from) quickChanges.push({ from, to, scene: order[i + 1] });
      }
    }
  }
  return { actors, quickChanges, characters: people.length };
}

/** `Doubling.colour`: the fewest colours for up to 24 vertices, else DSatur's. */
export function colourGraph(neighbours: readonly Set<number>[]): number[] {
  const n = neighbours.length;
  let best = dsatur(neighbours);
  const most = Math.max(...best, 0);
  if (n > 24 || most === 0) return best;
  const order = [...Array(n).keys()].sort((a, b) => neighbours[b].size - neighbours[a].size);
  for (let k = most; k > 0; k--) {
    const colours = new Array<number>(n).fill(-1);
    let steps = 0;
    const place = (i: number, used: number): boolean => {
      if (++steps > 200_000) return false;
      if (i === n) return true;
      const v = order[i];
      for (let c = 0; c < Math.min(k, used + 1); c++) {
        if ([...neighbours[v]].some((u) => colours[u] === c)) continue;
        colours[v] = c;
        if (place(i + 1, Math.max(used, c + 1))) return true;
        colours[v] = -1;
      }
      return false;
    };
    if (!place(0, 0)) break;
    best = colours;
  }
  return best;
}

function dsatur(neighbours: readonly Set<number>[]): number[] {
  const n = neighbours.length;
  const colours = new Array<number>(n).fill(-1);
  const saturation = (v: number) => new Set([...neighbours[v]].map((u) => colours[u]).filter((c) => c >= 0)).size;
  for (let step = 0; step < n; step++) {
    let pick = -1;
    for (let v = 0; v < n; v++) {
      if (colours[v] >= 0) continue;
      // Most saturated, then most neighbours, then the lowest index, as the Swift's key (saturation, degree, -index).
      if (pick < 0 || saturation(v) > saturation(pick) || (saturation(v) === saturation(pick) && neighbours[v].size > neighbours[pick].size)) pick = v;
    }
    const taken = new Set([...neighbours[pick]].map((u) => colours[u]));
    let c = 0;
    while (taken.has(c)) c++;
    colours[pick] = c;
  }
  return colours;
}
